import { RSVPResponse, RSVPData } from '../../types/invitation';
import { supabase } from './client';
import { DBRSVPResponse } from './types';
import { transformDBRSVPToApp } from './transforms';
import { invitationService } from './invitationService';
import { createLogger } from '../logger';

const rsvpLog = createLogger('rsvp');

export const rsvpService = {
  async submitRSVP(
    inviteCode: string,
    rsvpData: RSVPData,
    guestInfo?: { name: string; email: string; phone: string }
  ): Promise<string> {
    const invitation = await invitationService.getInvitationByCode(inviteCode);
    if (!invitation) throw new Error('邀請函不存在');

    const guestName = guestInfo?.name || 'Unknown Guest';
    const guestEmail = guestInfo?.email || '';
    const guestPhone = guestInfo?.phone || '';

    const dbRSVP: Omit<DBRSVPResponse, 'id' | 'submitted_at'> = {
      invitation_id: invitation.id,
      invite_code: inviteCode,
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      attending: rsvpData.attending,
      guest_count: rsvpData.guestCount,
      guest_side: 'groom',
      dietary_restrictions: rsvpData.dietaryRestrictions || null,
      special_requests: rsvpData.specialRequests || null,
      message: rsvpData.message || null,
      contact_phone: rsvpData.contactPhone || null,
      contact_email: rsvpData.contactEmail || null,
      needs_physical_invitation: rsvpData.needsPhysicalInvitation || false,
      postal_address: rsvpData.postalAddress || null,
    };

    const { data, error } = await supabase
      .from('rsvp_responses')
      .insert(dbRSVP)
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  },

  async getRSVPsByInviteCode(inviteCode: string, userId: string): Promise<RSVPResponse[]> {
    if (!userId) throw new Error('必須提供用戶 ID 來獲取 RSVP 列表');
    const { data, error } = await supabase.rpc('get_rsvps_by_invite_code', { p_invite_code: inviteCode });
    if (error) {
      rsvpLog.error('獲取 RSVP 列表失敗:', error);
      throw new Error(`無法獲取 RSVP 列表: ${error.message}`);
    }
    return data.map(transformDBRSVPToApp);
  },

  async getRSVPStats(inviteCode: string, userId: string) {
    if (!userId) throw new Error('必須提供用戶 ID 來獲取統計數據');
    const { data, error } = await supabase.rpc('get_rsvp_stats', { p_invite_code: inviteCode });
    if (error) {
      rsvpLog.error('獲取 RSVP 統計失敗:', error);
      throw new Error(`無法獲取統計數據: ${error.message}`);
    }
    const rawStats = data;
    const guestSideStats = rawStats.guestSideStats || { bride: { count: 0, totalGuests: 0 }, groom: { count: 0, totalGuests: 0 } };
    const totalAttendingGuests = guestSideStats.bride.totalGuests + guestSideStats.groom.totalGuests;
    guestSideStats.bride.percentage = totalAttendingGuests > 0 ? Math.round((guestSideStats.bride.totalGuests / totalAttendingGuests) * 100) : 0;
    guestSideStats.groom.percentage = totalAttendingGuests > 0 ? Math.round((guestSideStats.groom.totalGuests / totalAttendingGuests) * 100) : 0;
    return {
      total: rawStats.total,
      attending: rawStats.attending,
      declined: rawStats.declined,
      totalGuests: rawStats.totalGuests,
      guestSideStats: guestSideStats
    };
  },

  async deleteRSVP(rsvpId: string, userId: string): Promise<void> {
    if (!userId) throw new Error('用戶未登入');
    const { error } = await supabase.rpc('delete_rsvp_by_id', { p_rsvp_id: rsvpId });
    if (error) {
      rsvpLog.error('刪除 RSVP 回覆失敗:', error);
      throw new Error(`刪除失敗: ${error.message}`);
    }
  },

  async updateGuestCount(rsvpId: string, newGuestCount: number, userId: string): Promise<void> {
    if (!userId) throw new Error('用戶未登入');
    const { error } = await supabase.rpc('update_rsvp_details', { p_rsvp_id: rsvpId, p_guest_count: newGuestCount });
    if (error) {
      rsvpLog.error('更新賓客人數失敗:', error);
      throw new Error(`更新失敗: ${error.message}`);
    }
  },

  async updateGuestSide(rsvpId: string, guestSide: 'bride' | 'groom', userId: string): Promise<void> {
    if (!userId) throw new Error('用戶未登入');
    const { error } = await supabase.rpc('update_rsvp_details', { p_rsvp_id: rsvpId, p_guest_side: guestSide });
    if (error) {
      rsvpLog.error('更新賓客親友分類失敗:', error);
      throw new Error(`更新失敗: ${error.message}`);
    }
  },

  async updateGuestName(rsvpId: string, newGuestName: string, userId: string): Promise<void> {
    if (!userId) throw new Error('用戶未登入');
    const { error } = await supabase.rpc('update_rsvp_details', { p_rsvp_id: rsvpId, p_guest_name: newGuestName });
    if (error) {
      rsvpLog.error('更新賓客姓名失敗:', error);
      throw new Error(`更新失敗: ${error.message}`);
    }
  },

  async clearRSVPMessage(rsvpId: string, userId: string): Promise<void> {
    if (!userId) throw new Error('用戶未登入');
    const { error } = await supabase.rpc('update_rsvp_details', { p_rsvp_id: rsvpId, p_clear_message: true });
    if (error) {
      rsvpLog.error('清空 RSVP 留言失敗:', error);
      throw new Error(`更新失敗: ${error.message}`);
    }
  },

  async getPhysicalInvitationRSVPs(inviteCode: string, userId: string): Promise<RSVPResponse[]> {
    if (!userId) throw new Error('用戶未登入');
    const { data, error } = await supabase.rpc('get_physical_invitation_rsvps', { p_invite_code: inviteCode });
    if (error) {
      rsvpLog.error('查詢實體喜帖 RSVP 失敗:', error);
      throw new Error(`查詢失敗: ${error.message}`);
    }
    return data.map(transformDBRSVPToApp);
  },

  async getPhysicalInvitationStats(inviteCode: string, userId: string): Promise<{
    totalRequests: number;
    totalGuests: number;
    addressList: Array<{ guestName: string; guestCount: number; postalAddress: string; }>
  }> {
    rsvpLog.debug('取得實體喜帖統計:', inviteCode);
    try {
      const rsvps = await this.getPhysicalInvitationRSVPs(inviteCode, userId);
      const totalRequests = rsvps.length;
      const totalGuests = rsvps.reduce((sum, rsvp) => sum + rsvp.rsvpData.guestCount, 0);
      const addressList = rsvps
        .filter(rsvp => rsvp.rsvpData.postalAddress)
        .map(rsvp => ({
          guestName: rsvp.guestName,
          guestCount: rsvp.rsvpData.guestCount,
          postalAddress: rsvp.rsvpData.postalAddress!
        }));
      const result = {
        totalRequests,
        totalGuests,
        addressList
      };
      rsvpLog.info('實體喜帖統計:', totalRequests, '個請求,', totalGuests, '位賓客');
      return result;
    } catch (error) {
      rsvpLog.error('取得實體喜帖統計失敗:', error);
      throw error;
    }
  }
};
